/**
 * Activity log — every mutating request the activityLogger middleware captured.
 * Filter by actor / entity / action / method / search / date range.
 */
import { useEffect, useState } from 'react';
import { RefreshCw, Search, Filter } from 'lucide-react';
import api from '../../utils/api';
import ActivityLogTable from '../../components/ActivityLogTable';

export default function Activity() {
    const [items, setItems]   = useState([]);
    const [total, setTotal]   = useState(0);
    const [page, setPage]     = useState(1);
    const [limit]             = useState(50);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState({ q: '', method: '', entity: '', action: '', from: '', to: '' });

    const load = async (p = page) => {
        setLoading(true);
        try {
            const params = { ...filter, page: p, limit };
            Object.keys(params).forEach(k => params[k] === '' && delete params[k]);
            const { data } = await api.get('/activity', { params });
            setItems(data.items);
            setTotal(data.total);
            setPage(data.page);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(1); /* eslint-disable-next-line */ }, [JSON.stringify(filter)]);

    const pages = Math.max(1, Math.ceil(total / limit));

    return (
        <div className="space-y-4">
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-2xl font-extrabold">Activity log</h1>
                    <p className="text-sm text-ink-500">Every mutating request — who, what, when. {total.toLocaleString()} entries.</p>
                </div>
                <button className="ag-btn-ghost" onClick={() => load(page)}>
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Reload
                </button>
            </div>

            <div className="ag-card p-3 flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[200px]">
                    <label className="ag-label">Search</label>
                    <div className="relative">
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
                        <input className="ag-input pl-9" placeholder="actor name · path · note" value={filter.q} onChange={e => setFilter(f => ({ ...f, q: e.target.value }))} />
                    </div>
                </div>
                <div>
                    <label className="ag-label">Method</label>
                    <select className="ag-input w-28" value={filter.method} onChange={e => setFilter(f => ({ ...f, method: e.target.value }))}>
                        <option value="">All</option>
                        <option>POST</option><option>PATCH</option><option>PUT</option><option>DELETE</option>
                    </select>
                </div>
                <div>
                    <label className="ag-label">Entity</label>
                    <input className="ag-input w-32" placeholder="e.g. User, Lead" value={filter.entity} onChange={e => setFilter(f => ({ ...f, entity: e.target.value }))} />
                </div>
                <div>
                    <label className="ag-label">Action</label>
                    <input className="ag-input w-32" placeholder="e.g. approve" value={filter.action} onChange={e => setFilter(f => ({ ...f, action: e.target.value }))} />
                </div>
                <div>
                    <label className="ag-label">From</label>
                    <input type="date" className="ag-input w-40" value={filter.from} onChange={e => setFilter(f => ({ ...f, from: e.target.value }))} />
                </div>
                <div>
                    <label className="ag-label">To</label>
                    <input type="date" className="ag-input w-40" value={filter.to} onChange={e => setFilter(f => ({ ...f, to: e.target.value }))} />
                </div>
                <div className="ml-auto flex items-center gap-2 text-xs text-ink-500">
                    <Filter className="h-4 w-4" /> page {page} / {pages}
                </div>
            </div>

            <ActivityLogTable items={items} showActor />

            {pages > 1 && (
                <div className="flex items-center justify-center gap-2 text-sm">
                    <button className="ag-btn-ghost text-xs" disabled={page === 1} onClick={() => load(page - 1)}>← Prev</button>
                    <span className="text-ink-500">Page {page} of {pages}</span>
                    <button className="ag-btn-ghost text-xs" disabled={page === pages} onClick={() => load(page + 1)}>Next →</button>
                </div>
            )}
        </div>
    );
}
